import { useClickOutside } from '@hooks/useClickOutside';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import type { GatedFeature } from '@parlay-picker/shared/featureAccess';
import { BarChart3, ChevronDown } from 'lucide-react';
import { type ReactElement, useCallback, useRef, useState } from 'react';
import { ANALYTICS_TABS, type TabId } from '../../types/tabs';
import { TierBadge } from './TierBadge';
import UpgradeModal from './UpgradeModal';

interface AnalyticsDropdownProps {
  activeTab: TabId;
  setActiveTab: (tab: TabId) => void;
}

export function AnalyticsDropdown({
  activeTab,
  setActiveTab,
}: AnalyticsDropdownProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState<GatedFeature | null>(null);
  const { isLocked } = useFeatureAccess();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isAnalyticsActive = ANALYTICS_TABS.some((tab) => tab.id === activeTab);
  const activeAnalyticsTab = ANALYTICS_TABS.find((tab) => tab.id === activeTab);

  const close = useCallback(() => setIsOpen(false), []);
  useClickOutside(dropdownRef, close, isOpen);

  const handleTabClick = (tabId: TabId): void => {
    // Summary is always free; all other analytics tabs require pro
    if (tabId !== 'summary' && isLocked('analytics_advanced')) {
      setUpgradeFeature('analytics_advanced');
      setIsOpen(false);
      return;
    }
    setActiveTab(tabId);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`px-3 py-1.5 rounded-lg font-medium transition-colors flex items-center gap-2 shrink-0 whitespace-nowrap ${
          isAnalyticsActive
            ? 'bg-theme-primary-fill text-theme-on-primary-fill'
            : 'bg-theme-surface text-theme-text-secondary hover:bg-theme-surface-hover'
        }`}
      >
        <BarChart3 className="w-4 h-4" />
        {isAnalyticsActive ? activeAnalyticsTab?.label : 'Analytics'}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-48 bg-theme-surface rounded-lg shadow-lg py-1 z-20 border border-theme-border">
          {ANALYTICS_TABS.map((tab) => {
            const locked = tab.id !== 'summary' && isLocked('analytics_advanced');
            return (
              <button
                type="button"
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`w-full text-left px-4 py-2 text-sm transition-colors flex items-center justify-between ${
                  activeTab === tab.id
                    ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                    : locked
                      ? 'text-theme-text-tertiary hover:bg-theme-surface-hover'
                      : 'text-theme-text-secondary hover:bg-theme-surface-hover'
                }`}
              >
                {tab.label}
                {locked && <TierBadge feature="analytics_advanced" size="md" />}
              </button>
            );
          })}
        </div>
      )}

      {upgradeFeature && (
        <UpgradeModal
          mode="feature"
          feature={upgradeFeature}
          onDismiss={() => setUpgradeFeature(null)}
        />
      )}
    </div>
  );
}
